import React, { useEffect, useState } from "react";
import AlumniStudentsForm from "../forms/AlumniStudentsForm";
import CurrentStudentsForm from "../forms/CurrentStudentsForm";

// Define registration period
const REGISTRATION_START_DATE = new Date("2024-09-01T00:00:00");
const REGISTRATION_END_DATE = new Date("2025-03-31T23:59:59");

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("StudentRegistration error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong in StudentRegistration.</h1>;
    }

    return this.props.children;
  }
}

const StudentRegistration = () => {
  const [memberType, setMemberType] = useState(null);
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState(null);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isRegistrationNearing, setIsRegistrationNearing] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [selectedType, setSelectedType] = useState(null); // Tracks which button is clicked
  const [formVisible, setFormVisible] = useState(false); // Controls form visibility after delay

  useEffect(() => {
    const currentDate = new Date();
    const oneMonthBeforeStart = new Date(REGISTRATION_START_DATE);
    oneMonthBeforeStart.setMonth(oneMonthBeforeStart.getMonth() - 1);

    if (
      currentDate >= REGISTRATION_START_DATE &&
      currentDate <= REGISTRATION_END_DATE
    ) {
      setIsRegistrationOpen(true);
      setShowHeader(true);
    } else {
      setIsRegistrationOpen(false);
      setShowHeader(false);
    }

    if (
      currentDate >= oneMonthBeforeStart &&
      currentDate < REGISTRATION_START_DATE
    ) {
      setIsRegistrationNearing(true);
    } else {
      setIsRegistrationNearing(false);
    }
  }, []);

  const handleMemberTypeChange = (type) => {
    setSelectedType(type); // Change button color immediately
    setFormVisible(false); // Initially hide the form
    setTimeout(() => {
      setMemberType(type); // Show form after 2 seconds
      setFormVisible(true); // Make form visible after delay
    }, 2000); // Delay of 2 seconds
  };

  const handleAccessCodeSubmit = () => {
    const ACCESS_CODE = "Gambia2024";
    if (accessCode === ACCESS_CODE) {
      setAccessGranted(true);
      setError(null);
    } else {
      setError("Invalid access code. Please try again.");
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-100 py-12 px-4 md:px-8">
        {showHeader && (
          <div className="mb-8 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-600 mb-2">
              Student Registration Form
            </h1>
            <p className="text-gray-600 text-sm md:text-base lg:text-lg">
              Welcome to the Gam-Ind Student Association Data Collection
            </p>
          </div>
        )}

        {isRegistrationOpen ? (
          !accessGranted ? (
            <div className="bg-white shadow-sm rounded-md p-6 w-full max-w-md md:max-w-2xl lg:max-w-3xl">
              <h2 className="text-xl md:text-2xl font-semibold text-start text-gray-800 mb-4">
                Access Code
              </h2>
              <div className="mb-4">
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter the secret code"
                />
              </div>
              {error && <p className="text-red-600">{error}</p>}
              <button
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300 mb-2"
                onClick={handleAccessCodeSubmit}
              >
                Submit Code
              </button>
            </div>
          ) : (
            <div className="bg-white shadow-sm border-2 rounded-xl p-6 w-full max-w-md md:max-w-2xl lg:max-w-4xl">
              {!memberType ? (
                <div className="flex flex-col justify-center items-center mb-4">
                  <h2 className="text-xl md:text-2xl font-semibold text-center text-gray-700 mb-4">
                    Select your Membership Type
                  </h2>
                  {/* Member Type Selection */}
                  <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-x-4 md:space-y-0">
                    <button
                      className={`w-full md:w-auto px-6 py-3 rounded-lg transition-colors duration-300 ${
                        selectedType === "current"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-400 text-white"
                      }`}
                      onClick={() => handleMemberTypeChange("current")}
                      disabled={selectedType !== null} // Disable while waiting for the form
                    >
                      Current Member
                    </button>
                    <button
                      className={`w-full md:w-auto px-6 py-3 rounded-lg transition-colors duration-300 ${
                        selectedType === "alumni"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-400 text-white"
                      }`}
                      onClick={() => handleMemberTypeChange("alumni")}
                      disabled={selectedType !== null} // Disable while waiting for the form
                    >
                      Alumni Member
                    </button>
                  </div>
                </div>
              ) : formVisible ? (
                <>
                  {memberType === "current" && (
                    <CurrentStudentsForm memberType="current" />
                  )}
                  {memberType === "alumni" && (
                    <AlumniStudentsForm memberType="alumni" />
                  )}
                </>
              ) : null}
            </div>
          )
        ) : (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-8 md:p-12 text-center max-w-md md:max-w-2xl lg:max-w-3xl mx-auto w-full">
            <h2 className="text-3xl md:text-4xl font-bold text-yellow-600 mb-4">
              {isRegistrationNearing
                ? "Exciting News!"
                : "We are Sorry, Registration is Closed."}
            </h2>
            {isRegistrationNearing ? (
              <p className="text-gray-700 mb-6 text-lg md:text-xl">
                Application will be opening soon! The registration will open from{" "}
                <strong>October 1st, 2024</strong>.
                <br />
                Stay tuned!
              </p>
            ) : (
              <p className="text-gray-700 mb-4 text-lg">
                The registration period is currently closed. Please check back
                during the open registration period from{" "}
                <strong>October 1st to March 31st</strong>.
              </p>
            )}
            <button
              className={`px-6 py-3 w-full md:w-1/2 lg:w-1/3 mx-auto ${
                isRegistrationNearing
                  ? "bg-yellow-500 hover:bg-yellow-600"
                  : "bg-gray-400 cursor-not-allowed"
              } text-white font-semibold rounded-lg transition-colors duration-300`}
              disabled={!isRegistrationNearing}
            >
              {isRegistrationNearing ? "Get Ready!" : "Follow us for updates"}
            </button>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default StudentRegistration;


